/**
 * Session-start context blocks for the Realtime brain.
 *
 * Two independent sources of "live" information are folded in when the
 * agent's session opens:
 *   1) USER-KONTEXT — one-shot GPS fix, tells the agent whether the user
 *      is in the gorge, near it, in the region, or planning from afar.
 *   2) LIVE BUS-DIGEST — the next few bus departures Tscheppaschlucht ↔
 *      Ferlach at the moment the session starts (the 90%-case) so the
 *      agent answers the standard "when's the next bus?" question from
 *      its head without a tool round-trip.
 *
 * `fetchSessionContext()` is the public entry point — it returns a single
 * string that the controller appends to the base instructions before the
 * mint call. Silently degrades to partial context on error.
 *
 * No continuous tracking, no storage. Single fetch per session.
 */

import { getBusDigest } from './bus'
import { getProgressSummary } from '../api/progress'
import { haversine } from '../domain/geo'

const GORGE_CENTER = { lat: 46.49778, lon: 14.26374 } as const

/** weather-api — der eigene Wetterdienst (Alex 2026-07-29: „Wettervorhersage
 *  an sich hat für mich nichts mit ArTrack zu tun ... sollte eine eigene API
 *  sein"). Liefert einen fertigen deutschen `text`-Satz.
 *
 *  Vorher stand hier ArTracks `/weather`. Die VORHERSAGE lief seit dem 29.07.
 *  schon über weather-api (Werkzeug `weather_forecast`), die aktuelle Lage
 *  aber noch über ArTrack — zwei Quellen für dasselbe Thema, die auseinander-
 *  laufen können. Jetzt eine.
 *
 *  ArTracks `/weather` bleibt bestehen, es hat andere Abnehmer; kein Rückbau
 *  ohne deren Absprache. */
const WEATHER_URL = 'https://weather-api.arkserver.arkturian.com/weather?location=tscheppaschlucht'

const GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: false, // city-block accuracy is fine; saves battery & avoids prompt
  maximumAge: 60_000,
  timeout: 5000,
}

function buildContextLine(lat: number, lon: number): string {
  const dist = haversine(lat, lon, GORGE_CENTER.lat, GORGE_CENTER.lon)
  if (dist < 500) {
    return (
      `USER-KONTEXT: Der Besucher steht aktuell mitten in der Tscheppaschlucht ` +
      `(GPS-Position ca. ${Math.round(dist)} m vom Schlucht-Zentrum). ` +
      `Du darfst Hinweise zur unmittelbaren Umgebung geben, z.B. "Sie stehen ` +
      `gerade in der Nähe des Tschaukofalls". Schlage konkrete Routen vor.`
    )
  }
  if (dist < 3000) {
    return (
      `USER-KONTEXT: Der Besucher ist in der Nähe der Tscheppaschlucht ` +
      `(GPS-Position ca. ${Math.round(dist)} m vom Schlucht-Eingang). ` +
      `Eventuell auf dem Weg dorthin oder gerade angekommen. Sprich entsprechend.`
    )
  }
  if (dist < 50_000) {
    return (
      `USER-KONTEXT: Der Besucher ist in der Region (ca. ${Math.round(dist / 1000)} km ` +
      `vom Schlucht-Eingang entfernt). Tagesausflug möglich. Die Kartenansicht ` +
      `kann trotzdem virtuell am Empfangshaus bei Kilometer 0 starten. Diese ` +
      `Startposition ist kein GPS-Standort: Bei Routenfragen immer „vom ` +
      `Empfangshaus“ oder „ab Kilometer 0“ sagen und niemals die Luftlinie zum ` +
      `Besucher als Wegstrecke der Route ausgeben.`
    )
  }
  return (
    `USER-KONTEXT: Der Besucher ist nicht in der Schlucht ` +
    `(Entfernung ca. ${Math.round(dist / 1000)} km). Antworte für Pre-Trip-Planung. ` +
    `Die Kartenansicht kann virtuell am Empfangshaus bei Kilometer 0 starten; ` +
    `das ist kein GPS-Standort. Bei Routenfragen immer „vom Empfangshaus“ oder ` +
    `„ab Kilometer 0“ sagen, niemals die Luftlinie als Routenentfernung nennen.`
  )
}

/**
 * Test-Override: lets you simulate "I am at lat,lon" via URL parameter
 * `?gps=46.488,14.270` (in-gorge example). Useful for dev when you are
 * physically elsewhere but want to test the in-gorge agent behavior.
 * URL override beats real GPS — fall back to GPS only if no override.
 *
 * Some handy test positions:
 *   ?gps=46.4877,14.2638  → at Tschaukofall (in-gorge zone)
 *   ?gps=46.4889,14.2622  → at Kassa / entrance
 *   ?gps=46.6249,14.3052  → Klagenfurt (Region zone)
 *   ?gps=48.2082,16.3738  → Vienna (Pre-Trip zone)
 */
function readGpsOverride(): { lat: number; lon: number } | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  const gps = params.get('gps')
  if (!gps) return null
  const [latStr, lonStr] = gps.split(',').map((s) => s.trim())
  const lat = Number(latStr)
  const lon = Number(lonStr)
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
  return { lat, lon }
}

/**
 * Returns a one-line USER-KONTEXT block to append to the session instructions.
 * Silently falls back to "unknown" on any error / denial — never throws.
 */
export function fetchUserLocationContext(): Promise<string> {
  return new Promise((resolve) => {
    const override = readGpsOverride()
    if (override) {
      const line = buildContextLine(override.lat, override.lon)
      console.log('[tschauko] GPS-Override aktiv (?gps=):', override, '→', line)
      return resolve(line + ' [SIMULIERT via ?gps=Parameter]')
    }
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      return resolve(
        'USER-KONTEXT: Standort unbekannt (Browser unterstützt keine Geolocation). ' +
          'Die Kartenansicht kann virtuell am Empfangshaus bei Kilometer 0 starten; ' +
          'das ist kein GPS-Standort. Bei Routenfragen immer „vom Empfangshaus“ ' +
          'oder „ab Kilometer 0“ sagen. Antworte für Pre-Trip-Planung.',
      )
    }
    let resolved = false
    const finish = (s: string) => {
      if (resolved) return
      resolved = true
      resolve(s)
    }
    // Belt-and-braces timeout in case the browser hangs on the prompt.
    const guard = window.setTimeout(() => {
      finish(
        'USER-KONTEXT: Standort unbekannt (GPS-Abfrage Timeout). Die Kartenansicht ' +
          'kann virtuell am Empfangshaus bei Kilometer 0 starten; das ist kein ' +
          'GPS-Standort. Bei Routenfragen immer „vom Empfangshaus“ oder „ab ' +
          'Kilometer 0“ sagen. Antworte für Pre-Trip-Planung.',
      )
    }, GEO_OPTIONS.timeout! + 2000)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        window.clearTimeout(guard)
        finish(buildContextLine(pos.coords.latitude, pos.coords.longitude))
      },
      () => {
        window.clearTimeout(guard)
        finish(
          'USER-KONTEXT: Standort unbekannt (keine GPS-Freigabe). Die Kartenansicht ' +
            'kann virtuell am Empfangshaus bei Kilometer 0 starten; das ist kein ' +
            'GPS-Standort. Bei Routenfragen immer „vom Empfangshaus“ oder „ab ' +
            'Kilometer 0“ sagen. Antworte für Pre-Trip-Planung.',
        )
      },
      GEO_OPTIONS,
    )
  })
}

/**
 * Full session-start context: USER-KONTEXT (GPS) + LIVE BUS-DIGEST.
 *
 * The bus digest is computed synchronously from the bundled JSON and the
 * current local clock — no network. It's cheap enough to always include,
 * even if GPS fails / the user is far away, because "when's the next bus
 * back?" is one of the most common questions and answering it from the
 * head keeps latency down.
 *
 * Order matters: USER-KONTEXT first (agent's spatial situation), then
 * LIVE BUS-DIGEST (concrete departure times) — this way the model reads
 * "user is in the gorge" and then immediately sees the corresponding
 * departure times, priming the right answer.
 */
export async function fetchSessionContext(): Promise<string> {
  const [userLine, busLine, progressLine, weatherLine] = await Promise.all([
    fetchUserLocationContext(),
    Promise.resolve(safeBusDigest()),
    safeProgressSummary(),
    safeWeatherDigest(),
  ])
  // Order matters: USER-KONTEXT (spatial situation) first, then WETTER right
  // after it (how it is there right now — primes greeting/route/gear tips),
  // then concrete departure times and progress.
  const parts: string[] = [datumsZeile(), userLine]
  if (weatherLine) parts.push(`WETTER (Sessionstart): ${weatherLine}`)
  if (busLine) parts.push(`LIVE BUS-DIGEST (Sessionstart-Zeit): ${busLine}`)
  if (progressLine) parts.push(`BESUCHER-FORTSCHRITT: ${progressLine}`)
  return parts.join('\n\n')
}

/** Saisonfenster der Schlucht. Spiegelt die Kern-Fakten im Gehirn
 *  (`scripts/personas/tschauko.config.mjs`, „Saison (heute)"). Ändert sich die
 *  Saison, müssen BEIDE Stellen nach — hier steht sie, weil ein Modell
 *  Datumsvergleiche nicht zuverlässig rechnet. */
const SAISON = { vonMonat: 5, vonTag: 1, bisMonat: 10, bisTag: 31 } as const

function inSaison(d: Date): boolean {
  const m = d.getMonth() + 1, t = d.getDate()
  if (m < SAISON.vonMonat || m > SAISON.bisMonat) return false
  if (m === SAISON.vonMonat && t < SAISON.vonTag) return false
  if (m === SAISON.bisMonat && t > SAISON.bisTag) return false
  return true
}

/**
 * Welcher Tag ist heute — und liegen heute und morgen in der Saison?
 *
 * Anlass (Alex 29.07.): „Er kann meine Frage nicht beantworten, wenn ich ihn
 * frage, ob morgen die Tscheppaschlucht geöffnet ist." Der Führer wusste die
 * Saison (1. Mai bis 31. Oktober) und die Tageszeiten — aber NICHT, welcher
 * Tag heute ist. Damit war „ist morgen offen?" für ihn unbeantwortbar, und er
 * verwies ans Besucherzentrum. Keine Wissenslücke, eine fehlende Uhr.
 *
 * Die Einordnung wird HIER gerechnet und fertig hineingeschrieben, nicht dem
 * Modell überlassen: Datumsarithmetik („liegt der 30. Juli zwischen dem 1. Mai
 * und dem 31. Oktober") ist genau die Sorte Aufgabe, bei der ein Sprachmodell
 * gelegentlich danebenliegt — und hier hinge eine Anreise daran.
 */
function datumsZeile(): string {
  const jetzt = new Date()
  const morgen = new Date(jetzt.getTime() + 24 * 60 * 60 * 1000)
  const fmt = (d: Date) =>
    d.toLocaleDateString('de-AT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  const uhr = jetzt.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })
  const heuteOffen = inSaison(jetzt)
  const morgenOffen = inSaison(morgen)
  return [
    `DATUM (Sessionstart): Heute ist ${fmt(jetzt)}, ${uhr} Uhr. Morgen ist ${fmt(morgen)}.`,
    `SAISON-EINORDNUNG: Heute liegt ${heuteOffen ? 'INNERHALB' : 'AUSSERHALB'} der Saison (1. Mai bis 31. Oktober), morgen ${morgenOffen ? 'ebenfalls INNERHALB' : 'AUSSERHALB'}.`,
    `NUR FÜR DICH, NIE AUSSPRECHEN: Das Wort „Saison", diese Einordnung und ihre Begründung gehören in deinen Kopf, nicht in die Antwort. Gefragt wird „ist morgen offen?" — geantwortet wird „Ja, morgen ist von 8:30 bis 17:00 Uhr geöffnet." Kein „weil", kein „das liegt innerhalb", keine Herleitung. Ist der Tag außerhalb: „Nein, im Winter ist die Schlucht geschlossen — ab 1. Mai wieder." Den Unwetter-Vorbehalt nennst du NUR, wenn das aktuelle Wetter tatsächlich danach aussieht.`,
  ].join(' ')
}

function safeBusDigest(): string {
  try {
    return getBusDigest(new Date())
  } catch (e) {
    console.warn('[tschauko] bus digest failed:', e)
    return ''
  }
}

/** guide-api /progress/summary returns a ready-to-inject text block
 *  ("Bisher 2 Art(en) gefunden: …"). 3s cap so a slow backend doesn't
 *  block session-start. Empty string when nothing to inject — the
 *  caller then omits the whole block. */
async function safeProgressSummary(): Promise<string> {
  const ac = new AbortController()
  const t = window.setTimeout(() => ac.abort(), 3000)
  try {
    const s = await getProgressSummary(ac.signal)
    return s.has_progress ? s.text : ''
  } catch (e) {
    console.debug('[tschauko] progress summary skipped:', e)
    return ''
  } finally {
    window.clearTimeout(t)
  }
}

/** weather-api liefert ein fertig formuliertes `text`-Feld
 *  ("Aktuell in der Tscheppaschlucht ⛅ bewölkt, rund 21 °C, …"). 3s cap so a
 *  slow/absent backend never blocks session-start. Empty string on any
 *  error / 404 (endpoint not deployed yet) — the caller then omits the whole
 *  WETTER block, and the agent may honestly say it doesn't know the current
 *  weather and point to the visitor centre. */
async function safeWeatherDigest(): Promise<string> {
  const ac = new AbortController()
  const t = window.setTimeout(() => ac.abort(), 3000)
  try {
    const res = await fetch(WEATHER_URL, { signal: ac.signal })
    if (!res.ok) return ''
    const d = (await res.json()) as { text?: string }
    return typeof d.text === 'string' ? d.text.trim() : ''
  } catch (e) {
    console.debug('[tschauko] weather digest skipped:', e)
    return ''
  } finally {
    window.clearTimeout(t)
  }
}
